import { useState } from "react";
import { Sparkles, Copy, Share2, Download, ChevronDown, Check, MessageCircle, MessageSquare } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";

/**
 * Vermittler-Transaktionscode — einklappbare Karte. Immer sichtbar: Code + QR +
 * „Kopieren" + „Weiterleiten". Beim Aufklappen: Erläuterung + QR-Download.
 */
export default function TransactionCodeCard({ shortCode, shareUrl }) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    if (!shortCode) return null;
    const qrValue = shareUrl || shortCode;

    const shareText = `Mein Transaktions-Code: ${shortCode}${shareUrl ? `\n${shareUrl}` : ""}\nBeim Kauf eintragen — so wird der Umsatz mir zugeordnet.`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    const smsUrl = `sms:?&body=${encodeURIComponent(shareText)}`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(shortCode);
            setCopied(true);
            toast.success("Code kopiert");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Kopieren fehlgeschlagen");
        }
    };

    const share = async () => {
        if (navigator.share) {
            try {
                await navigator.share({ title: "Transaktions-Code", text: shareText, url: shareUrl || undefined });
                return;
            } catch { /* abgebrochen → Fallback */ }
        }
        window.location.href = `mailto:?subject=${encodeURIComponent("Mein Transaktions-Code")}&body=${encodeURIComponent(shareText)}`;
    };

    const downloadQr = () => {
        const svg = document.getElementById("txcode-qr-svg");
        if (!svg) return;
        const blob = new Blob([new XMLSerializer().serializeToString(svg)], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `transaktionscode-${shortCode}.svg`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="transaction-code-card">
            <button
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-2 px-4 py-3"
                data-testid="transaction-code-toggle"
            >
                <Sparkles className="w-4 h-4 text-[#FFCC00]" />
                <span className="font-display font-bold text-sm">Vermittler-Transaktionscode</span>
                <ChevronDown className={`w-4 h-4 ml-auto text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {/* Immer sichtbar: Code + QR + Kopieren + Weiterleiten */}
            <div className="px-4 pb-4 flex items-center gap-3 flex-wrap">
                <div className="bg-white border border-gray-200 rounded-sm p-1.5 shrink-0">
                    <QRCodeSVG id="txcode-qr-svg" value={qrValue} size={72} level="H" fgColor="#111827" bgColor="#FFFFFF" data-testid="transaction-code-qr" />
                </div>
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Transaktions-Code</div>
                    <div className="font-mono-num text-2xl font-black tracking-wider text-[#111827]" data-testid="transaction-code-value">{shortCode}</div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <button onClick={copy} className="inline-flex items-center gap-1.5 rounded-sm border-2 border-[#FFCC00] px-3 py-1.5 text-xs font-bold hover:bg-[#FFFBEB]" data-testid="transaction-code-copy">
                            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Kopieren
                        </button>
                        <button onClick={share} className="inline-flex items-center gap-1.5 rounded-sm bg-[#111827] text-white px-3 py-1.5 text-xs font-bold hover:bg-black" data-testid="transaction-code-share">
                            <Share2 className="w-3.5 h-3.5" /> Weiterleiten
                        </button>
                        <a
                            href={whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 rounded-sm bg-[#25D366] text-white px-3 py-1.5 text-xs font-bold hover:bg-[#1EBE5A]"
                            data-testid="transaction-code-whatsapp"
                        >
                            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                        </a>
                        <a
                            href={smsUrl}
                            className="inline-flex items-center gap-1.5 rounded-sm border border-gray-300 px-3 py-1.5 text-xs font-bold hover:border-[#111827]"
                            data-testid="transaction-code-sms"
                        >
                            <MessageSquare className="w-3.5 h-3.5" /> SMS
                        </a>
                    </div>
                </div>
            </div>

            {open && (
                <div className="px-4 pb-4 pt-1 border-t border-gray-100" data-testid="transaction-code-details">
                    <p className="text-xs text-gray-600">
                        Geben Sie diesen Code an Kunden weiter (telefonisch, persönlich oder per E-Mail). Beim Kauf trägt der Kunde den Code ein — bei erfolgreichem Geschäft erhalten Sie automatisch 50 Punkte.
                    </p>
                    <button onClick={downloadQr} className="mt-3 inline-flex items-center gap-1.5 rounded-sm border border-gray-300 px-3 py-1.5 text-xs font-bold hover:border-[#111827]" data-testid="transaction-code-qr-download">
                        <Download className="w-3.5 h-3.5" /> QR als SVG
                    </button>
                </div>
            )}
        </div>
    );
}
